import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import { FaMapMarkerAlt } from "react-icons/fa";
import api from "./api";
import Navbar from "./components/Navbar/Navbar";
import Sidebar from "./components/Sidebar/Sidebar";
import MainChat from "./components/MainChat/MainChat";
import ConversationPage from "./components/ConversationPage/ConversationPage";
import FeedbackBar from "./components/FeedbackBar/FeedbackBar";
import FeedbackPage from "./components/FeedbackPage/FeedbackPage";
import { Button } from "./components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { cn } from "./lib/utils";

// Portal config for different states
const PORTAL_CONFIG = {
  yourcoa: {
    name: 'Florida',
    color: '#418b4c',
    authUrl: 'https://yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  nv_yourcoa: {
    name: 'Nevada', 
    color: '#418b4c',
    authUrl: 'https://nv.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  ma_yourcoa: {
    name: 'Massachusetts',
    color: '#418b4c',
    authUrl: 'https://ma.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  az_yourcoa: {
    name: 'Arizona',
    color: '#97c54e',
    authUrl: 'https://az.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  nc_yourcoa: {
    name: 'North Carolina',
    color: '#97c54e',
    authUrl: 'https://nc.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  co_yourcoa: {
    name: 'Colorado',
    color: '#97c54e',
    authUrl: 'https://co.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  ny_yourcoa: {
    name: 'New York',
    color: '#418b4c',
    authUrl: 'https://ny.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  md_yourcoa: {
    name: 'Maryland',
    color: '#418b4c',
    authUrl: 'https://md.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  },
  nj_yourcoa: {
    name: 'New Jersey',
    color: '#418b4c',
    authUrl: 'https://nj.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=https://kaycha-ai.site/backend/authenticate/'
  }
};

// State abbreviation mapping
const STATE_ABBREVIATIONS = {
  'Florida': 'FL',
  'Nevada': 'NV',
  'Massachusetts': 'MA',
  'Arizona': 'AZ',
  'North Carolina': 'NC',
  'Colorado': 'CO',
  'New York': 'NY',
  'Maryland': 'MD',
  'New Jersey': 'NJ'
};

// State Icon Component - displays pin icon with state abbreviation
const StateIcon = ({ stateName, color }) => {
  const stateAbbr = STATE_ABBREVIATIONS[stateName] || stateName.substring(0, 2).toUpperCase();
  
  return (
    <div className="flex flex-col items-center gap-0.5 mb-1">
      <FaMapMarkerAlt 
        style={{ color: color, fontSize: '20px' }}
      />
      <span 
        className="text-xs font-bold"
        style={{ color: color }}
      >
        {stateAbbr}
      </span>
    </div>
  );
};

const AppWrapper = () => (
  <Router>
    <App />
  </Router>
);

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [chats, setChats] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [formData, setFormData] = useState({ message: "" });
  const [isTyping, setIsTyping] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [userPortal, setUserPortal] = useState(null);
  const [portalConfig, setPortalConfig] = useState(null);
  const messagesEndRef = useRef(null);

  // Initialize theme on mount
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = stored ? stored === 'dark' : prefersDark;
    
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Extract token from URL and store it in sessionStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");

    if (token) {
      sessionStorage.setItem("access_token", token);
      setIsAuthenticated(true);
      
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const portal = payload.portal;
        if (portal) {
          setUserPortal(portal);
          setPortalConfig({
            name: PORTAL_CONFIG[portal]?.name || portal.charAt(0).toUpperCase() + portal.slice(1),
            color: PORTAL_CONFIG[portal]?.color || '#007bff',
            authUrl: PORTAL_CONFIG[portal]?.authUrl || 'https://nv.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=http://kaycha-ai.site/backend/authenticate/'
          });
        } else {
          console.error("No portal found in token");
          setIsAuthenticated(false);
          sessionStorage.removeItem("access_token");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        setIsAuthenticated(false);
        sessionStorage.removeItem("access_token");
      }

      navigate("/", { replace: true });
    } else {
      const storedToken = sessionStorage.getItem("access_token");
      if (storedToken) {
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          const portal = payload.portal;
          if (portal) {
            setIsAuthenticated(true);
            setUserPortal(portal);
            setPortalConfig({
              name: PORTAL_CONFIG[portal]?.name || portal.charAt(0).toUpperCase() + portal.slice(1),
              color: PORTAL_CONFIG[portal]?.color || '#007bff',
              authUrl: PORTAL_CONFIG[portal]?.authUrl || 'https://nv.yourcoa.com/ai/auth?auth_code=def2f767f29d81828ea184e1fb39eaf2&success_url=http://kaycha-ai.site/backend/authenticate/'
            });
          } else {
            console.error("No portal found in stored token");
            setIsAuthenticated(false);
            sessionStorage.removeItem("access_token");
          }
        } catch (error) {
          console.error("Error decoding stored token:", error);
          setIsAuthenticated(false);
          sessionStorage.removeItem("access_token");
        }
      }
    }
  }, [location, navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("access_token");
    setIsAuthenticated(false);
    setUserPortal(null);
    setPortalConfig(null);
  };

  const fetchOngoingChat = async () => {
    try {
      const response = await api.get("/mainchat");
      setChats(response.data);
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      const fetchConversations = async () => {
        try {
          const response = await api.get("/conversations");
          setConversations(response.data);
        } catch (error) {
          console.error("Error fetching conversations:", error);
        }
      };

      fetchConversations();
      fetchOngoingChat();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chats, isTyping]);

  const handleInputChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (formData.message.trim()) {
      setIsTyping(true);
      try {
        await api.post(`/invoke`, { message: formData.message, id: activeConversationId });
        await fetchOngoingChat();
        setFormData({ message: "" });
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        setIsTyping(false);
      }
    }
  };

  const deletebtn = async () => {
    try {
      await api.delete("/clearchat");
      setChats([]);
      fetchOngoingChat();
    } catch (error) {
      console.error("Error clearing chat:", error);
    }
    window.location.reload();
  };

  const handleNewChat = async () => {
    try {
      // If there are messages in main chat, save them to conversations first
      if (chats.length > 0) {
        await api.delete("/clearchat");
        // Refresh conversations list to show the newly saved conversation
        const response = await api.get("/conversations");
        setConversations(response.data);
      }
      // Clear the main chat state
      setChats([]);
      // Reset active conversation
      setActiveConversationId(null);
      // Navigate to main chat page (in case we were viewing an old conversation)
      navigate("/");
    } catch (error) {
      console.error("Error creating new chat:", error);
    }
  };

  const resetActiveConversation = () => {
    setActiveConversationId(null);
  };

  const groupConversationsByDate = (conversations) => {
    // First, sort all conversations by created_on (most recent first), then by id (higher id = more recent)
    const sortedConversations = [...conversations].sort((a, b) => {
      const dateA = new Date(a.created_on);
      const dateB = new Date(b.created_on);
      
      // Sort by date first
      if (dateB.getTime() !== dateA.getTime()) {
        return dateB - dateA;
      }
      
      // If dates are the same, sort by id (higher id = created more recently)
      return b.id - a.id;
    });
    
    // Then group by date
    const grouped = sortedConversations.reduce((acc, conversation) => {
      const date = new Date(conversation.created_on).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(conversation);
      return acc;
    }, {});
    
    return grouped;
  };

  const groupedConversations = groupConversationsByDate(conversations);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const getCurrentConversationId = () => {
    const path = location.pathname;
    if (path.startsWith('/conversations/')) {
      return path.split('/')[2];
    }
    return activeConversationId;
  };

  const getCurrentMessages = () => {
    const path = location.pathname;
    if (path.startsWith('/conversations/')) {
      const currentConversation = conversations.find(conv => conv.id === parseInt(getCurrentConversationId()));
      return currentConversation?.messages || [];
    }
    return chats;
  };

  return (
    <div className="min-h-screen bg-background">
      {isAuthenticated ? (
        <>
          <Navbar 
            onLogout={handleLogout} 
            toggleSidebar={toggleSidebar} 
            currentPortal={userPortal}
            portalConfig={portalConfig}
          />
          <div className="flex pt-16 h-screen">
            <Sidebar
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={toggleSidebar}
              conversations={groupedConversations}
              activeConversationId={activeConversationId}
              setActiveConversationId={setActiveConversationId}
              handleNewChat={handleNewChat}
            />
            <main className="flex-1 md:ml-72 h-[calc(100vh-4rem)]">
              <Routes>
                <Route
                  path="/"
                  element={
                    <MainChat
                      chats={chats}
                      formData={formData}
                      handleInputChange={handleInputChange}
                      handleFormSubmit={handleFormSubmit}
                      deletebtn={deletebtn}
                      isTyping={isTyping}
                    />
                  }
                />
                <Route
                  path="/conversations/:id"
                  element={<ConversationPage resetActiveConversation={resetActiveConversation} />}
                />
                <Route 
                  path="/feedback" 
                  element={<FeedbackPage />}
                />  
              </Routes>
            </main>
            <FeedbackBar
              conversationId={getCurrentConversationId()}
              isOpen={isFeedbackOpen}
              onToggle={() => setIsFeedbackOpen(!isFeedbackOpen)}
              messages={getCurrentMessages()}
            />
          </div>
        </>
      ) : (
        <LoginPage />
      )}
    </div>
  );
};

// Separate Login Page Component
const LoginPage = () => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      {/* Logo and Welcome */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
          <FlaskConical className="h-8 w-8 text-primary" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">Welcome to PURELims AI</h1>
        <p className="text-muted-foreground">Your AI-powered lab assistant</p>
      </div>

      {/* Portal Selection Card */}
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Select Your State Portal</CardTitle>
          <CardDescription>Choose your state to continue to the login</CardDescription>
        </CardHeader>
        <div className="p-6 pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(PORTAL_CONFIG).map(([portalKey, config]) => (
              <Button
                key={portalKey}
                variant="outline"
                className={cn(
                  "h-auto py-4 flex flex-col items-center gap-1 transition-all hover:scale-[1.02]",
                  "hover:border-primary hover:bg-primary/5"
                )}
                style={{
                  '--hover-color': config.color,
                }}
                onClick={() => {
                  window.location.href = config.authUrl;
                }}
              >
                <StateIcon stateName={config.name} color={config.color} />
                <span className="font-medium">{config.name}</span>
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Footer */}
      <p className="mt-8 text-sm text-muted-foreground">
        Powered by Kaycha Labs
      </p>
    </div>
  );
};

export default AppWrapper;
